import React, { ChangeEvent } from 'react';

import {
  VenueHours,
  VenueDisposition,
  VenueMealPeriod,
  InvertedVenueHours,
  InvertedVenueHoursStatus,
  VenueDisplayOptions,
} from '@go-shared/types/plan';
import { HourFormat } from '@go-shared/types/brand';
import {
  timeToString,
  DAY_LENGTH,
  timeToMinutes,
  DEFAULT_TIMESTR_FORMAT,
} from '@go-shared/lib/time';
import { Time, TimeBlock } from '@go-shared/types/time';
import { getBlocksInRange, blockToTime, timeToBlockIndex } from '@go-shared/lib/grid';
import { TextButton, InlineIcon, Checkbox } from '@spark-labs/avocado-ui';
import { invertVenueHours, invertedVenueHoursToApiFormat } from '@go-shared/lib/plan';
import { PropertyType } from '@go-shared/types/property';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { BrandMealPeriod } from '@go-shared/lib/mealPeriods';
import { BubbleButton, BubbleFormGroup, BubbleInput, BubbleSelect } from './BubbleUI';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import McmVisibilityTable from './McmVisibilityTable';
import { Mcm } from '@go-shared/types/mcm';
import AudienceVisibilityTable from './AudienceVisibilityTable';
import { Audience } from '@go-shared/types/audience';
import { CallToActionsInstance } from '@go-shared/types/dailyProgram';
import { EVENT_DISPLAY_MAP, EVENT_DISPLAY_TYPES } from '@go-shared/lib/event';
import { CallToActionsManagement } from '@go-client/components/BasicForms/CallToActionsManagement';

const BLOCK_TIME_GAP = 15;

type EditableVenueMealPeriods = VenueMealPeriod & { selected: boolean };
type EditableInvertedVenueHours = InvertedVenueHours & { selected: boolean };

export interface VenueFormProps {
  venueData: VenueDisposition;
  template: VenueTemplate;
  propertyType: PropertyType;
  hourFormat: HourFormat;
  dayStart: Time;
  mealPeriods: BrandMealPeriod[];
  onlyVenueHours?: boolean;
  onlyOpen?: boolean;
  hoursTitle?: string;
  submitLabel?: string;
  onSubmit?: (
    venueHours: VenueHours[],
    mealPeriods: VenueMealPeriod[],
    displayOptions: VenueDisplayOptions,
    callToActions: CallToActionsInstance,
    dynamic?: boolean,
    mcm?: Mcm,
    audience?: Audience
  ) => void;
  onVenueHoursUpdate?: (venueHours: VenueHours[]) => void;
  hideSubmitButton?: boolean;
}

export interface VenueFormState {
  mcm?: Mcm;
  audience?: Audience;
  venueHours: EditableInvertedVenueHours[];
  mealPeriods: EditableVenueMealPeriods[];
  displayOptions: VenueDisplayOptions;
  dynamic?: boolean;
  callToActions: CallToActionsInstance;
}

// Note that `open` really means "Open without service", which we display as "No Service", and any
// other status can be interpreted as "Available", which we display as "Open".
function venueHoursToString(status: InvertedVenueHoursStatus): string {
  return status === 'open' ? 'No Service' : 'Open';
}

export default class VenueForm extends React.Component<VenueFormProps, VenueFormState> {
  public readonly state: VenueFormState = {
    mcm: undefined,
    audience: undefined,
    venueHours: [],
    mealPeriods: [],
    displayOptions: {},
    callToActions: { syncWithTemplate: true, values: [] },
  };

  private dayStartMinutes: number;
  private timeBlocks: TimeBlock[];
  private timeBlockOptions: Array<React.ReactElement<HTMLOptionElement>>;
  private hoursTitle: string = 'Hours';

  constructor(props: VenueFormProps) {
    super(props);

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleAddVenueHoursPeriodClick = this.handleAddVenueHoursPeriodClick.bind(this);
    this.handleAddMealPeriodClick = this.handleAddMealPeriodClick.bind(this);
    this.handleVenueHoursRowClick = this.handleVenueHoursRowClick.bind(this);
    this.handleMealPeriodsRowClick = this.handleMealPeriodsRowClick.bind(this);
    this.handleVenueHoursStartTimeChange = this.handleVenueHoursStartTimeChange.bind(this);
    this.handleVenueHoursEndTimeChange = this.handleVenueHoursEndTimeChange.bind(this);
    this.handleVenueHoursStateChange = this.handleVenueHoursStateChange.bind(this);
    this.handleRemoveVenueHoursRow = this.handleRemoveVenueHoursRow.bind(this);
    this.handleMealPeriodsStartTimeChange = this.handleMealPeriodsStartTimeChange.bind(this);
    this.handleMealPeriodsEndTimeChange = this.handleMealPeriodsEndTimeChange.bind(this);
    this.handleMealPeriodsStateChange = this.handleMealPeriodsStateChange.bind(this);
    this.handleMealPeriodsReservationChange = this.handleMealPeriodsReservationChange.bind(this);
    this.handleRemoveMealPeriodsRow = this.handleRemoveMealPeriodsRow.bind(this);
    this.handleMcmUpdate = this.handleMcmUpdate.bind(this);
    this.handleAudUpdate = this.handleAudUpdate.bind(this);
    this.handleCallToActionsUpdate = this.handleCallToActionsUpdate.bind(this);

    this.dayStartMinutes = timeToMinutes(this.props.dayStart);
    this.timeBlocks = getBlocksInRange(
      0,
      DAY_LENGTH,
      BLOCK_TIME_GAP,
      this.dayStartMinutes,
      this.props.hourFormat
    );

    this.timeBlockOptions = this.timeBlocks.map(b => (
      <option key={b.index} value={b.index}>
        {b.name}
      </option>
    ));

    this.state = {
      mcm: this.props.venueData.mcm,
      audience: this.props.venueData.audience,
      venueHours: invertVenueHours(this.props.venueData.hours, this.dayStartMinutes).map(h => {
        return { ...h, selected: false };
      }),
      mealPeriods: this.props.venueData.mealPeriods.map(p => {
        return { ...p, selected: false };
      }),
      displayOptions: { ...this.props.venueData.displayOptions },
      dynamic: this.props.venueData.dynamic,
      callToActions: this.props.venueData.callToActions,
    };

    if (this.props.hoursTitle) {
      this.hoursTitle = this.props.hoursTitle;
    }
  }

  public render() {
    const mealPeriods = this.props.mealPeriods.filter(mp => mp.type === this.props.propertyType);
    let levelLabel = getPropertyConfiguration(this.props.propertyType, 'levelLabel');

    const venueHoursPiece = (
      <BubbleFormGroup className="col-span-6" label={this.hoursTitle}>
        <table className="border w-full text-left bg-white">
          <thead>
            <tr className="text-teal-dark">
              <th>Start</th>
              <th>End</th>
              <th>State</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {this.state.venueHours.map((h, i) => (
              <tr
                key={i}
                className="text-grey-dark"
                onClick={() => this.handleVenueHoursRowClick(i)}
              >
                <td>
                  {h.selected ? (
                    <select
                      onChange={this.handleVenueHoursStartTimeChange}
                      className="bg-grey-lightest w-4/5"
                      value={timeToBlockIndex(h.startTime, BLOCK_TIME_GAP, this.dayStartMinutes)}
                    >
                      {this.timeBlockOptions}
                    </select>
                  ) : (
                    timeToString(h.startTime, this.props.hourFormat, DEFAULT_TIMESTR_FORMAT)
                  )}
                </td>
                <td>
                  {h.selected ? (
                    <select
                      onChange={this.handleVenueHoursEndTimeChange}
                      className="bg-grey-lightest w-4/5"
                      value={timeToBlockIndex(h.endTime, BLOCK_TIME_GAP, this.dayStartMinutes)}
                    >
                      {this.timeBlockOptions}
                    </select>
                  ) : (
                    timeToString(h.endTime, this.props.hourFormat, DEFAULT_TIMESTR_FORMAT)
                  )}
                </td>
                <td>
                  {h.selected ? (
                    <select
                      onChange={this.handleVenueHoursStateChange}
                      className="bg-grey-lightest w-4/5"
                      value={h.status}
                    >
                      {(this.props.onlyOpen
                        ? (['free'] as InvertedVenueHoursStatus[])
                        : (['open', 'free'] as InvertedVenueHoursStatus[])
                      ).map(status => (
                        <option key={status} value={status}>
                          {venueHoursToString(status)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    venueHoursToString(h.status)
                  )}
                </td>
                <td>
                  <TextButton
                    onClick={e => {
                      e.preventDefault();
                      this.handleRemoveVenueHoursRow(i);
                    }}
                    className="no-underline"
                  >
                    <InlineIcon className="w-4 h-4 text-grey-dark" name={'trash'} />
                  </TextButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TextButton
          onClick={this.handleAddVenueHoursPeriodClick}
          className="text-teal-dark mt-2 hover:text-teal-darker"
        >
          <div className="flex items-center">
            <InlineIcon name={'plus'} className="mr-1" />
            <span>Add Period</span>
          </div>
        </TextButton>
      </BubbleFormGroup>
    );

    const mealPeriodsPiece = (
      <BubbleFormGroup className="col-span-6" label="Meal Periods">
        <table className="border w-full text-left bg-white">
          <thead>
            <tr className="text-teal-dark">
              <th>Start</th>
              <th>End</th>
              <th>Type</th>
              <th>Reservation</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {this.state.mealPeriods.map((p, i) => (
              <tr
                key={i}
                className="text-grey-dark"
                onClick={() => this.handleMealPeriodsRowClick(i)}
              >
                <td>
                  {p.selected ? (
                    <select
                      onChange={this.handleMealPeriodsStartTimeChange}
                      className="bg-grey-lightest w-4/5"
                      value={timeToBlockIndex(p.startTime, BLOCK_TIME_GAP, this.dayStartMinutes)}
                    >
                      {this.timeBlockOptions}
                    </select>
                  ) : (
                    timeToString(p.startTime, this.props.hourFormat, DEFAULT_TIMESTR_FORMAT)
                  )}
                </td>
                <td>
                  {p.selected ? (
                    <select
                      onChange={this.handleMealPeriodsEndTimeChange}
                      className="bg-grey-lightest w-4/5"
                      value={timeToBlockIndex(p.endTime, BLOCK_TIME_GAP, this.dayStartMinutes)}
                    >
                      {this.timeBlockOptions}
                    </select>
                  ) : (
                    timeToString(p.endTime, this.props.hourFormat, DEFAULT_TIMESTR_FORMAT)
                  )}
                </td>
                <td>
                  {p.selected ? (
                    <select
                      onChange={e => this.handleMealPeriodsStateChange(e, mealPeriods)}
                      className="bg-grey-lightest w-4/5"
                      value={p.brandMealPeriodId}
                    >
                      {mealPeriods.map(mp => (
                        <option key={mp._id} value={mp._id}>
                          {mp.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    p.name
                  )}
                </td>
                <td>
                  <Checkbox
                    checked={!!this.state.mealPeriods[i].needsReservation}
                    onChange={e => this.handleMealPeriodsReservationChange(i, e)}
                  />
                </td>
                <td>
                  <TextButton
                    className="border-none mb-1 no-underline"
                    onClick={e => {
                      e.preventDefault();
                      this.handleRemoveMealPeriodsRow(i);
                    }}
                  >
                    <InlineIcon className="w-4 h-4 text-grey-dark" name={'trash'} />
                  </TextButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TextButton
          onClick={() => this.handleAddMealPeriodClick(mealPeriods)}
          className="text-teal-dark mt-2 hover:text-teal-darker"
        >
          <div className="flex items-center">
            <InlineIcon name={'plus'} className="mr-1" />
            <span>Add Period</span>
          </div>
        </TextButton>
      </BubbleFormGroup>
    );

    return (
      <>
        <div className="p-4 grid grid-cols-6 gap-4">
          {this.props.onlyVenueHours ? (
            venueHoursPiece
          ) : (
            <>
              {venueHoursPiece}
              {mealPeriodsPiece}
              <hr className="col-span-6 my-1" />
              <BubbleFormGroup label="Open Hours Venue Name" className="col-span-2">
                <BubbleInput
                  block
                  type="text"
                  value={this.state.displayOptions.displayName}
                  onValue={val =>
                    this.setState({
                      displayOptions: {
                        ...this.state.displayOptions,
                        displayName: val,
                      },
                    })
                  }
                />
              </BubbleFormGroup>
              <BubbleFormGroup label={`Open Hours ${levelLabel} Name`} className="col-span-2">
                <BubbleInput
                  block
                  type="text"
                  value={this.state.displayOptions.deckDisplayName}
                  onValue={val =>
                    this.setState({
                      displayOptions: {
                        ...this.state.displayOptions,
                        deckDisplayName: val,
                      },
                    })
                  }
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Time Display" className="col-span-2">
                <BubbleSelect
                  block
                  allowUndefined={false}
                  labelExtractor={id => EVENT_DISPLAY_MAP[id].label}
                  value={this.state.displayOptions.timeDisplay}
                  options={EVENT_DISPLAY_TYPES}
                  onValue={val =>
                    this.setState({
                      displayOptions: {
                        ...this.state.displayOptions,
                        timeDisplay: val,
                      },
                    })
                  }
                />
              </BubbleFormGroup>
              <div className="col-span-6">
                <div className="flex items-center relative">
                  <BubbleInput
                    type="checkbox"
                    checked={!!this.state.dynamic}
                    inputClassName="stretched-link"
                    onValue={chk => this.setState({ dynamic: chk })}
                  />
                  <span className="ml-1">Dynamic Venue</span>
                </div>
              </div>
              <hr className="col-span-6 my-1" />
              <div className="col-span-6">
                <CallToActionsManagement
                  sourceCtas={this.state.callToActions}
                  parentCtas={this.props.template.callToActions}
                  onUpdate={(newCta: CallToActionsInstance) =>
                    this.handleCallToActionsUpdate(newCta)
                  }
                />
              </div>
              <hr className="col-span-6 my-1" />
              <BubbleFormGroup label="Endpoints" className="col-span-3">
                <McmVisibilityTable
                  isTemplate={false}
                  sourceMcm={this.state.mcm}
                  parentMcm={this.props.template.mcm}
                  onUpdateMcm={this.handleMcmUpdate}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Audience" className="col-span-3">
                <AudienceVisibilityTable
                  isTemplate={false}
                  sourceAud={this.state.audience}
                  parentAud={this.props.template.audience}
                  onUpdateAudience={this.handleAudUpdate}
                />
              </BubbleFormGroup>
            </>
          )}
        </div>
        {!this.props.hideSubmitButton && (
          <div className="p-3 w-full sticky bottom-0 bg-white border-t text-right z-10">
            <BubbleButton variant="navy" onClick={this.handleFormSubmit} icon="floppy-disk">
              {this.props.submitLabel || 'Save Changes'}
            </BubbleButton>
          </div>
        )}
      </>
    );
  }

  private onUpdateVenueHours() {
    if (this.props.onVenueHoursUpdate) {
      this.props.onVenueHoursUpdate(
        invertedVenueHoursToApiFormat(this.state.venueHours, this.dayStartMinutes).map(vh => {
          return {
            startTime: vh.startTime,
            endTime: vh.endTime,
            status: vh.status,
          };
        })
      );
    }
  }

  private handleAddVenueHoursPeriodClick() {
    this.setState(
      {
        venueHours: this.state.venueHours
          .map(vh => {
            return { ...vh, selected: false };
          })
          .concat([
            {
              startTime: this.props.dayStart,
              endTime: this.props.dayStart,
              status: 'free',
              selected: true,
            },
          ]),
      },
      () => {
        this.onUpdateVenueHours();
      }
    );
  }

  private handleVenueHoursRowClick(index: number) {
    this.setState({
      venueHours: this.state.venueHours.map((vh, i) => {
        return { ...vh, selected: i === index };
      }),
    });
    this.onUpdateVenueHours();
  }

  private handleMealPeriodsRowClick(index: number) {
    this.setState({
      mealPeriods: this.state.mealPeriods.map((mp, i) => {
        return { ...mp, selected: i === index };
      }),
    });
  }

  private handleFormSubmit() {
    if (this.props.onSubmit) {
      this.props.onSubmit(
        invertedVenueHoursToApiFormat(this.state.venueHours, this.dayStartMinutes).map(vh => {
          return {
            startTime: vh.startTime,
            endTime: vh.endTime,
            status: vh.status,
          };
        }),
        this.state.mealPeriods.map(mp => {
          return {
            startTime: mp.startTime,
            endTime: mp.endTime,
            name: mp.name,
            code: mp.code,
            needsReservation: mp.needsReservation,
            brandMealPeriodId: mp.brandMealPeriodId,
          };
        }),
        this.state.displayOptions,
        this.state.callToActions,
        this.state.dynamic,
        this.state.mcm,
        this.state.audience
      );
    }
  }

  private handleVenueHoursStartTimeChange(event: ChangeEvent<HTMLSelectElement>) {
    let selectedRowIndex = this.state.venueHours.findIndex(vh => vh.selected);
    if (selectedRowIndex === -1) return;

    let selectedHours = this.state.venueHours[selectedRowIndex];
    let timeBlock = this.timeBlocks[parseInt(event.target.value, 10)];

    selectedHours.startTime = blockToTime(timeBlock.index, BLOCK_TIME_GAP, this.dayStartMinutes);

    this.setState({ venueHours: this.state.venueHours });
    this.onUpdateVenueHours();
  }

  private handleVenueHoursEndTimeChange(event: ChangeEvent<HTMLSelectElement>) {
    let selectedRowIndex = this.state.venueHours.findIndex(vh => vh.selected);
    if (selectedRowIndex === -1) return;

    let selectedHours = this.state.venueHours[selectedRowIndex];
    let timeBlock = this.timeBlocks[parseInt(event.target.value, 10)];

    selectedHours.endTime = blockToTime(timeBlock.index, BLOCK_TIME_GAP, this.dayStartMinutes);

    this.setState({ venueHours: this.state.venueHours });
    this.onUpdateVenueHours();
  }

  private handleVenueHoursStateChange(event: ChangeEvent<HTMLSelectElement>) {
    let selectedRowIndex = this.state.venueHours.findIndex(vh => vh.selected);
    if (selectedRowIndex === -1) return;

    let selectedHours = this.state.venueHours[selectedRowIndex];
    selectedHours.status = event.target.value as InvertedVenueHoursStatus;

    this.setState({ venueHours: this.state.venueHours });
  }

  private handleRemoveVenueHoursRow(index: number) {
    this.state.venueHours.splice(index, 1);

    this.setState({
      venueHours: this.state.venueHours.map(vh => {
        return { ...vh, selected: false };
      }),
    });
    this.onUpdateVenueHours();
  }

  private handleMcmUpdate(newMcm: Mcm) {
    this.setState({ mcm: newMcm });
  }

  private handleAudUpdate(newAud: Audience) {
    this.setState({ audience: newAud });
  }

  // FIXME: Mostly copied from above
  private handleAddMealPeriodClick(allMealPeriods: BrandMealPeriod[]) {
    this.setState({
      mealPeriods: this.state.mealPeriods
        .map(mp => {
          return { ...mp, selected: false };
        })
        .concat([
          {
            startTime: this.props.dayStart,
            endTime: this.props.dayStart,
            name: allMealPeriods[0].name,
            code: allMealPeriods[0].code,
            selected: true,
            brandMealPeriodId: allMealPeriods[0]._id,
          },
        ]),
    });
  }

  private handleMealPeriodsStartTimeChange(event: ChangeEvent<HTMLSelectElement>) {
    let selectedRowIndex = this.state.mealPeriods.findIndex(mp => mp.selected);
    if (selectedRowIndex === -1) return;

    let selectedPeriod = this.state.mealPeriods[selectedRowIndex];
    let timeBlock = this.timeBlocks[parseInt(event.target.value, 10)];

    selectedPeriod.startTime = blockToTime(timeBlock.index, BLOCK_TIME_GAP, this.dayStartMinutes);

    this.setState({ mealPeriods: this.state.mealPeriods });
  }

  private handleMealPeriodsEndTimeChange(event: ChangeEvent<HTMLSelectElement>) {
    let selectedRowIndex = this.state.mealPeriods.findIndex(mp => mp.selected);
    if (selectedRowIndex === -1) return;

    let selectedPeriod = this.state.mealPeriods[selectedRowIndex];
    let timeBlock = this.timeBlocks[parseInt(event.target.value, 10)];

    selectedPeriod.endTime = blockToTime(timeBlock.index, BLOCK_TIME_GAP, this.dayStartMinutes);

    this.setState({ mealPeriods: this.state.mealPeriods });
  }

  private handleMealPeriodsStateChange(
    event: ChangeEvent<HTMLSelectElement>,
    allMealPeriods: BrandMealPeriod[]
  ) {
    let selectedRowIndex = this.state.mealPeriods.findIndex(mp => mp.selected);
    if (selectedRowIndex === -1) return;

    let selectedPeriod = this.state.mealPeriods[selectedRowIndex];

    // Find meal period based on the selected code
    let mealPeriod = allMealPeriods.find(mp => mp._id === event.target.value);

    if (!mealPeriod) return;

    selectedPeriod.name = mealPeriod.name;
    selectedPeriod.code = mealPeriod.code;
    selectedPeriod.brandMealPeriodId = mealPeriod._id;

    this.setState({ mealPeriods: this.state.mealPeriods });
  }

  private handleMealPeriodsReservationChange(
    row: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    if (row >= this.state.mealPeriods.length) return;

    let selectedPeriod = this.state.mealPeriods[row];

    selectedPeriod.needsReservation = event.target.checked;
    this.setState({ mealPeriods: this.state.mealPeriods });
  }

  private handleRemoveMealPeriodsRow(index: number) {
    this.state.mealPeriods.splice(index, 1);

    this.setState({
      mealPeriods: this.state.mealPeriods.map(mp => {
        return { ...mp, selected: false };
      }),
    });
  }

  private handleCallToActionsUpdate(newCta: CallToActionsInstance) {
    this.setState({ callToActions: newCta });
  }
}
